/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// This class allows integration with OCAPI Stores Resource
// https://documentation.b2c.commercecloud.salesforce.com/DOC1/index.jsp?topic=%2Fcom.demandware.dochelp%2FOCAPI%2Fcurrent%2Fshop%2FResources%2FStores.html&cp=0_16_3_18
// This implementations coverts CAPI requests to OCAPI requests as there are fundamental differences between the APIS
// One major difference is OCAPI uses snake_case and CAPI uses camelCase for this reason you will see a utility function in here that convert
// from camelCase to snake_case - camelCaseKeysToUnderscore
// createOcapiFetch is another utility function that returns the response from OCAPI in the fromat returned from CAPI
// Another utility function - checkRequiredParameters is used to check if the parameters or body objects necessary for a call are
// present in the request before making it

import { CommerceApiConfig } from '~/lib/data-contract/salesforce';
import { camelCaseKeysToUnderscore, checkRequiredParameters, createOcapiFetch } from './utils';

class OcapiShopperStores {
    private fetch;

    constructor(config: CommerceApiConfig) {
        this.fetch = createOcapiFetch(config);
    }

    async getStores(...args: any) {
        const required = ['postalCode', 'countryCode'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const params = camelCaseKeysToUnderscore(args[0].parameters);
        const cleanedForSalesForce = Object.entries(params).reduce((acc: any, [key, value]) => {
            if (value) {
                acc[key] = value;
            }
            return acc;
        }, {});

        return this.fetch(
            `stores?${new URLSearchParams(cleanedForSalesForce).toString()}`,
            'GET',
            args,
            'getStores'
        );
    }

    async getStore(...args: any) {
        try {
            const required = ['id'];
            const requiredParametersError = checkRequiredParameters(args[0], required);
            if (requiredParametersError) {
                return requiredParametersError;
            }
            const params = camelCaseKeysToUnderscore(args[0].parameters);
            return this.fetch(
                `stores/${params.id}?${new URLSearchParams(params).toString()}`,
                'GET',
                args,
                'getStores'
            );
        } catch (e) {
            console.error(e);
        }
    }
    async getStoreList(...args: any) {
        const params = camelCaseKeysToUnderscore(args[0].parameters);
        return this.fetch(
            `stores?select=(**)&${new URLSearchParams(params).toString()}`,
            'GET',
            args,
            'getStores'
        );
    }
}

export default OcapiShopperStores;
